import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Package,
  Tag,
  Ticket,
  ShoppingCart,
  Store,
  CreditCard,
  Truck,
  Smartphone,
  StickyNote,
  Newspaper,
  XCircle,
} from 'lucide-react';

interface MarketplaceActionNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  Mail: <Mail className="w-5 h-5" />,
  Package: <Package className="w-5 h-5" />,
  Tag: <Tag className="w-5 h-5" />,
  Ticket: <Ticket className="w-5 h-5" />,
  ShoppingCart: <ShoppingCart className="w-5 h-5" />,
  Store: <Store className="w-5 h-5" />,
  CreditCard: <CreditCard className="w-5 h-5" />,
  Truck: <Truck className="w-5 h-5" />,
  Smartphone: <Smartphone className="w-5 h-5" />,
  StickyNote: <StickyNote className="w-5 h-5" />,
  Newspaper: <Newspaper className="w-5 h-5" />,
};

const MarketplaceActionNode: React.FC<NodeProps<MarketplaceActionNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    const c = data.config || {};
    const statusLabel: Record<string, string> = { processing: 'Em separação', shipped: 'Enviado', delivered: 'Entregue', cancelled: 'Cancelado' };
    switch (data.type) {
      case 'marketplace_update_order':
        if (c.trackingCode) return `Rastreio: ${c.trackingCode}`;
        return c.orderStatus ? `Status: ${statusLabel[c.orderStatus] || c.orderStatus}` : null;
      case 'marketplace_add_tag':
        return c.tagName ? `Etiqueta: ${c.tagName}` : null;
      case 'marketplace_notify_buyer':
        return c.subject ? `E-mail: ${String(c.subject).substring(0, 28)}` : null;
      case 'marketplace_add_note':
        return c.note ? `Nota: ${String(c.note).substring(0, 28)}` : null;
      case 'marketplace_publish_blog_post':
        if (c.mode === 'ai') return c.topic ? `IA: ${String(c.topic).substring(0, 30)}` : 'Escrever com IA';
        return c.title ? `Post: ${String(c.title).substring(0, 30)}` : null;
      default:
        return null;
    }
  };

  const configPreview = getConfigPreview();

  return (
    <div
      className={`relative min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-indigo-500 shadow-indigo-100 dark:shadow-indigo-900/30'
          : 'border-indigo-200 dark:border-indigo-800 hover:border-indigo-400'
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-indigo-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <Store className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Marketplace</span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            {iconMap[data.icon || 'Package'] || <Package className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.type === 'marketplace_create_ticket' ? (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400"><XCircle className="w-3 h-3" /> Não existe no marketplace</span>
            ) : data.isConfigured ? (
              <span className="text-xs text-indigo-600 dark:text-indigo-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {configPreview && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {configPreview}
            </p>
          </div>
        )}

        <div className="mt-2 flex items-center justify-end">
          <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full font-medium">
            DevSkin Marketplace
          </span>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-indigo-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />
    </div>
  );
};

export default memo(MarketplaceActionNode);
